// 文件清单看板：按大类分卡片，每类文件一个方块（已传绿 / 必需未传红 / 可选未传灰），
// 显示传上来的文件、对应字段及是否已提取；顶部「上传目录」整批上传后由后端自动归到方块。
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ExtractedField, FieldChatMessage, ManifestField, ManifestFileReq, ManifestSchema, TaskDTO, TaskFileDTO } from "@csp/shared";
import { applyFileFlags, conditionText, fromFileText, resolveRequired } from "@csp/shared";
import { api, errorText } from "../lib/api";
import { useLoad } from "../lib/hooks";
import { useAuth } from "../lib/auth";
import { useMediaToken } from "../lib/media";
import { mediaUrl } from "../lib/net";
import { isJunkFile, uploadName, uploadTaskFile } from "../lib/upload";
import { Badge } from "./Badges";
import { AddFieldDialog, AddFileDialog, EditFileDialog, schemaNeedsReread } from "./FileSchemaDialogs";
import { FileGuideDialog } from "./FileGuideDialog";
import { useFilePreview } from "./FilePreview";
import { CopyCheckDialog } from "./CopyCheckDialog";

const ASSIGNED: Record<string, string> = { rule: "按文件名", ai: "AI 识别", manual: "人工" };
const OTHER_GROUP = "其他";

function fieldDone(f: ExtractedField | undefined): boolean {
  return !!f && (f.state === "ok" || f.state === "manual") && !!f.value;
}

/** 额外的 tab（如准备页的「信息提取」），排在文件大类后面 */
export interface BoardTab { key: string; label: string; tone?: string; badge?: string; content: ReactNode }

/** 底部「还缺」点某一项时发的事件（detail = 缺项原文，如「字段：身份证签发日期（多份文件不一致，请选择）」） */
export const FOCUS_MISSING = "csp:focus-missing";
export function focusMissing(item: string) { window.dispatchEvent(new CustomEvent(FOCUS_MISSING, { detail: item })); }

/** 冲突的几个值和各自来自哪份文件 */
function conflictText(ex: ExtractedField): string {
  const seen = new Map<string, string>();
  for (const c of ex.candidates ?? []) if (c.value != null && !seen.has(String(c.value))) seen.set(String(c.value), c.fileName.split("／").pop() ?? "");
  return [...seen].map(([v, f]) => `${v}${f ? `（${f}）` : ""}`).join(" / ");
}

export function FileBoard({ task, canUpload, onChanged, extraTabs = [], noteCard = null }: { task: TaskDTO; canUpload: boolean; onChanged: () => void; extraTabs?: BoardTab[]; noteCard?: ReactNode }) {
  const typeQ = useLoad(() => api.type(task.typeId), [task.typeId, task.files.length, task.extraction?.finishedAt]);
  const token = useMediaToken();
  const preview = useFilePreview();
  const [checkId, setCheckId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  // 「正在识别 / 正在提取」是一次性提示：提取跑完（结束时间变了）就清掉，不然一直挂着像卡住
  const exDone = task.extraction?.status === "done" ? task.extraction.finishedAt : null;
  useEffect(() => { if (exDone) setMsg(""); }, [exDone]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ManifestField | null>(null);
  const [addFileTo, setAddFileTo] = useState<string | null>(null);
  const [editCat, setEditCat] = useState<string | null>(null);
  const [addingField, setAddingField] = useState(false);
  const [tab, setTab] = useState<string | null>(null);
  const [guideFor, setGuideFor] = useState<string | null>(null);
  const isAdmin = useAuth().user?.role === "admin";
  // 条件必填按当前值算：已确定的 manifest 值优先，没有就用提取到的值
  // 由文件决定的字段（如 担保人是否在职 ← 担保人在职证明）按已传文件算
  const uploadedNow = task.files.filter((f) => f.state === "synced").map((f) => f.key);
  const rawValues: Record<string, unknown> = { ...Object.fromEntries((task.extraction?.fields ?? []).filter((e) => e.value != null).map((e) => [e.key, e.value])), ...(task.manifest ?? {}) };
  const curValues = typeQ.data ? applyFileFlags(typeQ.data.manifestSchema, rawValues, uploadedNow) : rawValues;
  const schema = typeQ.data ? resolveRequired(typeQ.data.manifestSchema, curValues) : undefined;
  const cats = schema?.files ?? [];
  const fields = schema?.fields ?? [];
  const extracted = task.extraction?.fields ?? [];
  const files = task.files;
  const filesOf = (key: string) => files.filter((f) => f.key === key);
  const fieldsOf = (key: string) => fields.filter((f) => (f.sourceFiles ?? []).includes(key));
  const value = (key: string): ExtractedField | undefined => {
    const f = fields.find((x) => x.key === key);
    // 由文件决定的字段：不提取、不手填，值就是有没有传那类文件
    if (f?.fromFile) return { key, label: f.label, required: f.required, value: String(curValues[key]), state: "manual", source: null, candidates: [] };
    return extracted.find((e) => e.key === key);
  };

  // 页面底部「还缺 N 项」点某一项：字段 → 切到「补字段」并打开这个字段；文件 → 切到「补文件」
  useEffect(() => {
    const onFocus = (e: Event) => {
      const item = String((e as CustomEvent<string>).detail ?? "");
      const m = /^字段：(.+?)(（.*）)?$/.exec(item);
      if (m) { const f = fields.find((x) => x.label === m[1]); setTab("fields"); if (f) setEditing(f); }
      else if (item.startsWith("文件：")) setTab("files");
      document.querySelector("[data-testid=file-board]")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener(FOCUS_MISSING, onFocus);
    return () => window.removeEventListener(FOCUS_MISSING, onFocus);
  }, [fields]);

  // 上传目录：全部先进「其他材料」，后端提取时按文件名 / AI 归到方块
  const uploadDir = async (list: FileList | null) => {
    const picked = Array.from(list ?? []).filter((f) => !isJunkFile(f));
    if (!picked.length) return;
    setBusy(true); setError(null);
    const failed: string[] = [];
    for (const [i, f] of picked.entries()) {
      setMsg(`上传中（${i + 1}/${picked.length}）${uploadName(f)}`);
      try { await uploadTaskFile(task.id, "other", f); } catch (e) { failed.push(`${uploadName(f)}：${errorText(e)}`); }
    }
    setMsg(failed.length ? "" : `已上传 ${picked.length} 份，正在自动归类和提取字段…`);
    if (failed.length) setError(`${failed.length} 份失败：${failed.join("；")}`);
    setBusy(false);
    // 类型还没有清单：传完就让 AI 从材料识别文件类别和字段
    if (blank && isAdmin && picked.length > failed.length) await discover();
    onChanged();
  };
  const discover = async () => {
    setBusy(true); setError(null);
    try { await api.discoverTask(task.id); setMsg("AI 正在读材料，识别文件类别和字段（约 1 分钟）…"); onChanged(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  // 删文件类别：它的文件回到「未归类」，字段不再关联它
  const removeCat = async (key: string) => {
    if (!schema) return;
    for (const f of filesOf(key)) await api.assignFile(f.id, "other");
    await saveSchema({ ...schema, version: schema.version + 1, files: schema.files.filter((c) => c.key !== key), fields: schema.fields.map((f) => (f.sourceFiles?.includes(key) ? { ...f, sourceFiles: f.sourceFiles.filter((k) => k !== key) } : f)) });
  };
  // 改文件清单 / 字段（任务类型，所有任务生效），改完重新提取以重算缺项
  const saveSchema = async (next: ManifestSchema) => {
    const reread = schemaNeedsReread(schema, next);
    await api.updateType(task.typeId, { manifestSchema: next });
    await typeQ.reload();
    await api.extractTask(task.id, !reread).catch(() => undefined);
    onChanged();
  };
  const uploadTo = async (key: string, list: FileList | null) => {
    const picked = Array.from(list ?? []).filter((f) => !isJunkFile(f));
    if (!picked.length) return;
    setBusy(true); setError(null);
    try { for (const f of picked) await uploadTaskFile(task.id, key, f); onChanged(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  const remove = async (file: TaskFileDTO) => {
    setBusy(true); setError(null);
    try { await api.removeFile(file.id); onChanged(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  const assign = async (file: TaskFileDTO, key: string) => {
    setBusy(true); setError(null);
    try { await api.assignFile(file.id, key); onChanged(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  const groups = [...new Set(cats.map((c) => c.group || OTHER_GROUP))];
  const required = cats.filter((c) => c.required);
  const haveRequired = required.filter((c) => filesOf(c.key).some((f) => f.state === "synced"));
  const requiredFields = fields.filter((f) => f.required);
  const doneFields = requiredFields.filter((f) => fieldDone(value(f.key)));
  const unsorted = filesOf("other");
  // 这次没有文件能提供的字段：没关联任何文件，或关联的文件这次都没传（担保人邮箱这类）。单独一张卡片，点开手填或设置关联
  const uploadedKeys = new Set(files.filter((f) => f.state === "synced").map((f) => f.key));
  const looseFields = fields.filter((f) => !(f.sourceFiles ?? []).some((k) => uploadedKeys.has(k)));
  const extracting = task.extraction?.status === "running";
  // 准备了多久：从第一次传文件算，按天；超过 3 天黄、超过 7 天红
  const firstUpload = files.reduce<string | null>((m, f) => (f.uploadedAt && (!m || f.uploadedAt < m) ? f.uploadedAt : m), null);
  // 按日历天算（本地时区）：昨天传的今天就是第 1 天
  const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const ageDays = firstUpload ? Math.round((dayStart(new Date()) - dayStart(new Date(firstUpload))) / 86_400_000) : null;

  const fileChip = (f: TaskFileDTO) => (
    <div key={f.id} className="fb-file">
      <button type="button" className="fb-open" title={`预览 ${f.name}`} translate="no" disabled={!token} onClick={() => preview.open({ url: mediaUrl(`/api/files/${f.id}`, token), name: f.name, contentType: f.contentType })}>{f.name.split("／").pop()}</button>
      {f.state !== "synced" && <Badge tone={f.state === "failed" ? "red" : "amber"}>{f.state === "failed" ? "同步失败" : "处理中"}</Badge>}
      {f.assignedBy && <span className="small muted">{ASSIGNED[f.assignedBy]}</span>}
      {f.uploadCopy && (f.uploadCopy.needsCheck && !f.uploadCopy.confirmedAt
        ? <button type="button" className="btn btn-sm btn-warn" data-testid="file-shrunk-check" title={f.uploadCopy.how} onClick={() => setCheckId(f.id)}>已压缩 {humanSize(f.size)} → {humanSize(f.uploadCopy.size)}，待确认看得清</button>
        : <button type="button" className="fb-open small ok-text" data-testid="file-shrunk" title={`${f.uploadCopy.how}${f.uploadCopy.confirmedBy ? `；${f.uploadCopy.confirmedBy} 确认看得清` : ""}，点开对比`} onClick={() => setCheckId(f.id)}>已压缩 {humanSize(f.size)} → {humanSize(f.uploadCopy.size)}{f.uploadCopy.confirmedAt ? " ✓ 已确认" : ""}</button>)}
      {f.sizeIssue && <button type="button" className="fb-open small err" data-testid="file-size-issue" onClick={() => setCheckId(f.id)}>{f.sizeIssue}（换一份 / 联系管理员）</button>}
      {canUpload && <button type="button" className="fb-del" aria-label={`删除 ${f.name}`} title="删除这份文件" disabled={busy} onClick={() => { if (confirm(`删除 ${f.name.split("／").pop()}？`)) void remove(f); }}>×</button>}
      {canUpload && (
        <select aria-label={`改类别 ${f.name}`} value={f.key} disabled={busy} onChange={(e) => void assign(f, e.target.value)}>
          {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          <option value="other">未归类</option>
        </select>
      )}
    </div>
  );
  const fieldRow = (f: ManifestField) => {
    const v = value(f.key);
    const ok = fieldDone(v);
    if (f.fromFile && schema) return <li key={f.key} className="ok-text" title={fromFileText(f.fromFile, schema)}>✓ {f.label}：{v?.value === "true" ? "是" : "否"}<span className="small muted">（{fromFileText(f.fromFile, schema)}）</span></li>;
    return (
      <li key={f.key} className={ok ? "ok-text" : f.required ? "err" : "muted"}>
        <button type="button" title={ok ? `${v!.value}（点击查看 / 修改）` : "未提取到，点击填写或设置"} onClick={() => setEditing(f)}>
          {ok ? "✓" : "✗"} {f.label}{f.required && !ok ? "（必填）" : ""}
        </button>
      </li>
    );
  };
  const block = (c: ManifestFileReq) => {
    const mine = filesOf(c.key);
    const have = mine.some((f) => f.state === "synced");
    const tone = have ? "ok" : c.required ? "bad" : "idle";
    const fs = fieldsOf(c.key);
    return (
      <div key={c.key} className={`fb-block fb-${tone}`} data-testid={`file-block-${c.key}`}>
        <div className="row" style={{ gap: 6 }}>
          {/* 必需的标题后面红 *，可选的标灰「可选」（2026-10-31 业主定） */}
          <b>{c.label}{c.required && <span className="fb-req" title="必需上传，没有这份文件不能开始" aria-label="必需">*</span>}</b>
          {!c.required && !c.requiredWhen && <span className="small muted">可选</span>}
          {!c.required && c.requiredWhen && schema && <span className="small muted" data-testid="cond-required">{conditionText(c.requiredWhen, schema)}</span>}
          {c.submitToGov && <Badge tone="amber" title="这份文件要原样上传到政府门户">上传政府</Badge>}
          <span className="spacer" />
          {isAdmin && <button type="button" className="fb-edit" title="改名称 / 大类，或删除这个类别" aria-label={`修改 ${c.label}`} onClick={() => setEditCat(c.key)}>✎</button>}
          <button type="button" className={`fb-info${c.guide?.clientMessage || c.guide?.samples?.length ? "" : " fb-info-empty"}`} title="怎么准备这份文件：样本、怎么拿、发给客户的话、要求" aria-label={`怎么准备 ${c.label}`} onClick={() => setGuideFor(c.key)}>i</button>
        </div>
        {c.hint && <p className="small muted" style={{ margin: "4px 0" }}>{c.hint}</p>}
        {c.maxKB && <p className="small muted" style={{ margin: "2px 0" }}>门户限制每份 ≤ {humanSize(c.maxKB * 1024)}{c.accept?.length ? `，${c.accept.join(" / ")}` : ""}；超过的自动压缩</p>}
        {mine.length ? mine.map(fileChip) : <p className="small" style={{ margin: "6px 0" }}>{c.required ? "还没上传" : "未上传（可选）"}</p>}
        {fs.length > 0 && <ul className="fb-fields">{fs.map(fieldRow)}</ul>}
        {canUpload && (
          <label className="btn btn-sm" style={busy ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
            上传文件<input type="file" multiple hidden aria-label={`上传 ${c.label}`} disabled={busy} onChange={(e) => { void uploadTo(c.key, e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>
    );
  };

  // 顶部 tab：第一个「待补」自动汇总还缺的文件和字段；后面每个文件方块一个 tab（按大类排），再加未归类 / 手填字段
  const missingFiles = required.filter((c) => !filesOf(c.key).some((f) => f.state === "synced"));
  const missingReq = requiredFields.filter((f) => !fieldDone(value(f.key)));
  const missingOpt = fields.filter((f) => !f.required && !fieldDone(value(f.key)));
  // 补字段：有文件对应的缺字段分「必填 / 可选」两张卡，没有文件对应的全放「无文件对应」卡，互不重复
  const looseKeys = new Set(looseFields.map((f) => f.key));
  const reqFromFile = missingReq.filter((f) => !looseKeys.has(f.key));
  const optFromFile = missingOpt.filter((f) => !looseKeys.has(f.key));
  const optionalMissing = cats.filter((c) => !c.required && !filesOf(c.key).some((f) => f.state === "synced"));
  const catTone = (c: ManifestFileReq) => {
    const have = filesOf(c.key).some((f) => f.state === "synced");
    if (!have) return c.required ? "bad" : "idle";
    return fieldsOf(c.key).some((f) => f.required && !fieldDone(value(f.key))) ? "warn" : "ok";
  };
  const fileTodo = missingFiles.length + (cats.length ? unsorted.length : 0);
  // 「补文件 / 补字段」等第一次上传、归类和提取都跑完才出现（还没传就满屏红没意义，2026-10-31 业主定）
  const analyzed = files.some((f) => f.state === "synced") && !extracting && !files.some((f) => f.state !== "synced" && f.state !== "failed");
  // 类型还没有文件清单和字段（新建类型、第一次探索前）：不显示 0/0 和「补文件 / 补字段」，只说明怎么做
  const blank = !!schema && !cats.length && !fields.length;
  const showTodo = !blank && (analyzed || !groups.length);
  const tabKeys = [...(showTodo ? ["files", "fields"] : []), ...groups.map((g) => `group:${g}`), ...extraTabs.map((t) => t.key)];
  // 默认：分析完了缺文件先看「补文件」；文件齐了但缺字段看「补字段」；都齐了 / 还没分析看第一个大类（没有大类就看「补文件」）
  const current = tab && tabKeys.includes(tab) ? tab : showTodo && fileTodo ? "files" : showTodo && missingReq.length ? "fields" : groups.length ? `group:${groups[0]}` : "files";
  // 分析刚跑完：回到默认（有缺的就切到「补文件 / 补字段」）
  const wasAnalyzed = useRef(analyzed);
  useEffect(() => {
    if (analyzed && !wasAnalyzed.current) setTab(null);
    wasAnalyzed.current = analyzed;
  }, [analyzed]);
  // 缺字段说明：关联文件传了 = 识别问题；没传 = 补哪类文件；没关联 = 手填
  const why = (f: ManifestField) => {
    const ex = value(f.key);
    if (ex?.state === "conflict") return `文件之间不一致：${conflictText(ex)}。点字段选对的那个`;
    const src = f.sourceFiles ?? [];
    const label = (k: string) => cats.find((c) => c.key === k)?.label ?? k;
    const have = src.filter((k) => uploadedKeys.has(k));
    if (have.length) return `已上传「${have.map(label).join("」「")}」但没识别出`;
    if (src.length) return `补「${src.map(label).join("」或「")}」可自动提取，或直接填写`;
    return "没有文件提供，点击填写";
  };
  // 补字段的一张卡：标题「必填 / 可选」，里面列缺的字段和原因，点字段填写 / AI 识别
  const fieldBox = (title: string, list: ManifestField[], tone: "bad" | "idle", emptyText: string) => (
    <div className={`fb-block fb-${list.length ? tone : "ok"}`} data-testid={`field-box-${title}`}>
      <div className="row" style={{ gap: 6 }}><b>{title}</b><span className="small muted">缺 {list.length} 项</span></div>
      {list.length ? (
        <ul className="fb-fields">
          {list.map((f) => (
            <li key={f.key} className={f.required || value(f.key)?.state === "conflict" ? "err" : "muted"} data-testid={`fb-missing-${f.key}`}>
              <button type="button" onClick={() => setEditing(f)}>✗ {f.label}</button>
              <div className="small muted fb-why">{why(f)}</div>
            </li>
          ))}
        </ul>
      ) : <p className="small ok-text">✓ {emptyText}</p>}
    </div>
  );
  const tabBtn = (key: string, label: string, tone: string, extra?: string, tip?: string) => (
    <button key={key} type="button" role="tab" aria-selected={current === key} title={tip} className={`fb-tab fb-tab-${tone}${current === key ? " active" : ""}`} onClick={() => setTab(key)} data-testid={`fb-tab-${key}`}>
      <span className="fb-dot" />{label}{extra ? <span className="fb-tab-n">{extra}</span> : null}
    </button>
  );
  // tab 上的 ✗ 只数必填（和颜色、底部「还缺 N 项」一致），可选的没值在 tab 里面看
  const missingFieldsOf = (key: string) => fieldsOf(key).filter((f) => f.required && !fieldDone(value(f.key))).length;

  return (
    <div className="card" data-testid="file-board">
      {preview.node}
      {checkId && (() => {
        const f = files.find((x) => x.id === checkId);
        return f ? <CopyCheckDialog taskId={task.id} file={f} token={token} onChanged={onChanged} onClose={() => setCheckId(null)} /> : null;
      })()}
      <div className="row">
        <h2 style={{ margin: 0 }}>文件清单</h2>
        {ageDays !== null && <span className={`fb-age fb-age-${ageDays > 7 ? "red" : ageDays > 3 ? "amber" : "ok"}`} data-testid="prep-age" title={`从第一次传文件（${new Date(firstUpload!).toLocaleString()}）算起`}>{ageDays === 0 ? "今天开始" : `已 ${ageDays} 天`}</span>}
        {!blank && <>
          <span className={haveRequired.length === required.length ? "ok-text" : "err"}>必需文件 {haveRequired.length}/{required.length}</span>
          <span className="small muted"><span className="fb-req">*</span> = 必需上传</span>
          <span className={doneFields.length === requiredFields.length ? "ok-text" : "err"}>必填字段 {doneFields.length}/{requiredFields.length}</span>
        </>}
        {extracting && <Badge tone="amber">归类 / 提取中…</Badge>}
        <span className="spacer" />
        <span className="small muted" title="含子文件夹，按文件名和内容自动归类。tab 上的点：绿 = 齐了，橙 = 有文件但必填字段没识别全，红 = 必需还缺，灰 = 可选未传；数字 = 还缺几份必需文件，必需文件都传了就是还缺几个必填字段">整个文件夹一次上传，自动归类</span>
        {canUpload && (
          <label className="btn" style={busy ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
            上传文件<input type="file" multiple hidden aria-label="上传文件" disabled={busy} onChange={(e) => { void uploadDir(e.target.files); e.target.value = ""; }} />
          </label>
        )}
        {canUpload && (
          <label className="btn btn-primary" style={busy ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
            上传目录<input type="file" hidden aria-label="上传目录" disabled={busy} {...{ webkitdirectory: "", directory: "" }} onChange={(e) => { void uploadDir(e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>
      {msg && <p className="small">{msg}</p>}
      {error && <p className="notice err">{error}</p>}
      {typeQ.error && <p className="notice err">{typeQ.error}</p>}

      <div className="fb-tabs" role="tablist" aria-label="文件清单">
        {/* 「补文件 / 补字段」还有必需项要补时排最前；补齐了就挪到大类后面 */}
        {showTodo && fileTodo > 0 && tabBtn("files", "补文件", "bad", String(fileTodo))}
        {showTodo && missingReq.length > 0 && tabBtn("fields", "补字段", "bad", String(missingReq.length))}
        {showTodo && (fileTodo > 0 || missingReq.length > 0) && <span className="fb-tab-sep" aria-hidden />}
        {groups.map((g) => {
          const gc = cats.filter((c) => (c.group || OTHER_GROUP) === g);
          const tones = gc.map(catTone);
          const tone = tones.includes("bad") ? "bad" : tones.includes("warn") ? "warn" : tones.includes("ok") ? "ok" : "idle";
          // 数字：先数还没传的必需文件；必需文件都传了，再数还没识别出的必填字段（同一字段几份文件都能提供，去重）；这个大类一份都没传、也没有必需文件就不显示
          const noFile = gc.filter((c) => c.required && !filesOf(c.key).some((f) => f.state === "synced")).length;
          const anyUploaded = gc.some((c) => filesOf(c.key).some((f) => f.state === "synced"));
          const missF = new Set(gc.flatMap((c) => fieldsOf(c.key).filter((f) => f.required && !fieldDone(value(f.key))).map((f) => f.key)));
          const n = noFile || (anyUploaded ? missF.size : 0);
          const tip = noFile ? `还有 ${noFile} 份必需文件没上传` : n ? `文件都传了，还有 ${n} 个必填字段没识别出` : undefined;
          return tabBtn(`group:${g}`, g, tone, n ? String(n) : undefined, tip);
        })}
        {showTodo && !missingReq.length && tabBtn("fields", "补字段", "ok")}
        {showTodo && !fileTodo && tabBtn("files", "补文件", "ok")}
        {extraTabs.length > 0 && <span className="fb-tab-push" aria-hidden />}
        {extraTabs.map((t) => tabBtn(t.key, t.label, t.tone ?? "idle", t.badge))}
      </div>

      <div className="fb-panel" role="tabpanel">
        {blank && current === "files" && (
          <>
            <p className="notice" data-testid="blank-schema">这个类型还没有文件清单：把材料都传上来，AI 会读一遍，识别出文件类别和字段（都先算可选），不对的你再改；探索跑完再按页面实际要求定哪些必需。</p>
            {extracting
              ? <p className="small"><Badge tone="amber">识别中…</Badge> AI 正在读材料，识别文件类别和字段（约 1 分钟）</p>
              : isAdmin && canUpload && unsorted.length > 0 && <p><button className="btn btn-primary" disabled={busy} onClick={() => void discover()} data-testid="discover">从材料识别文件类别和字段</button></p>}
            <div className="fb-grid">
              {unsorted.length > 0 && (
                <div className="fb-block fb-ok">
                  <b>已上传的文件（{unsorted.length}）</b>
                  {unsorted.map(fileChip)}
                </div>
              )}
              {noteCard}
            </div>
          </>
        )}
        {!blank && current === "files" && (
          missingFiles.length + optionalMissing.length + unsorted.length === 0 ? <p className="ok-text">文件都齐了。</p> : (
            <div className="fb-grid">
              {missingFiles.map(block)}
              {optionalMissing.map(block)}
              {unsorted.length > 0 && (
                <div className={`fb-block ${cats.length ? "fb-bad" : "fb-ok"}`}>
                  <b>{cats.length ? "未归类" : "已上传的文件"}（{unsorted.length}）</b>
                  {cats.length > 0 && <p className="small muted" style={{ margin: "4px 0" }}>{extracting ? "正在自动归类…" : "认不出属于哪个文件，请手动选择类别"}</p>}
                  {unsorted.map(fileChip)}
                </div>
              )}
            </div>
          )
        )}
        {current === "fields" && (
          <>
            <p className="small muted">点字段直接填写，或让 AI 助理按你说的方法重新识别。</p>
            <div className="fb-grid">
              {fieldBox("必填", reqFromFile, "bad", "必填字段都有值了")}
              {fieldBox("可选", optFromFile, "idle", "没有缺的可选字段")}
              <div className={`fb-block fb-${looseFields.some((f) => f.required && !fieldDone(value(f.key))) ? "bad" : looseFields.length ? "ok" : "idle"}`} data-testid="field-box-loose">
                <div className="row" style={{ gap: 6 }}><b>无文件对应</b><span className="small muted">{looseFields.length} 项</span></div>
                <p className="small muted" style={{ margin: "4px 0" }}>没关联文件、或关联的文件这次没传：点字段手填，或写进「补充说明」</p>
                {looseFields.length > 0 && <ul className="fb-fields">{looseFields.map(fieldRow)}</ul>}
                {isAdmin && schema && <button className="btn btn-sm" onClick={() => setAddingField(true)}>＋ 新增字段</button>}
              </div>
              {noteCard}
            </div>
          </>
        )}
        {groups.filter((g) => current === `group:${g}`).map((g) => (
          <div key={g} className="fb-grid">
            {cats.filter((c) => (c.group || OTHER_GROUP) === g).map(block)}
            {isAdmin && <button type="button" className="fb-block fb-idle fb-add" onClick={() => setAddFileTo(g)}>＋ 添加文件</button>}
          </div>
        ))}
        {extraTabs.filter((t) => t.key === current).map((t) => <div key={t.key}>{t.content}</div>)}
      </div>

      {editing && schema && (
        <FieldDialog
          taskId={task.id}
          chat={task.fieldChats?.[editing.key] ?? []}
          onTaught={onChanged}
          field={editing}
          extracted={value(editing.key)}
          canEdit={canUpload}
          canSetRequired={isAdmin}
          cats={cats}
          onClose={() => setEditing(null)}
          onSave={async (v) => { await api.setExtractedField(task.id, editing.key, v); onChanged(); }}
          onConfig={async ({ required, sourceFiles }) => {
            await saveSchema({ ...schema, version: schema.version + 1, fields: schema.fields.map((x) => (x.key === editing.key ? { ...x, required, sourceFiles } : x)) });
          }}
        />
      )}
      {guideFor && schema && cats.some((c) => c.key === guideFor) && (
        <FileGuideDialog typeId={task.typeId} schema={schema} cat={cats.find((c) => c.key === guideFor)!} taskFiles={files} isAdmin={isAdmin} token={token}
          onChanged={async () => { await typeQ.reload(); }} onClose={() => setGuideFor(null)} />
      )}
      {editCat && schema && cats.some((c) => c.key === editCat) && (
        <EditFileDialog schema={schema} cat={cats.find((c) => c.key === editCat)!} onSave={saveSchema} onRemove={() => removeCat(editCat)} onClose={() => setEditCat(null)} />
      )}
      {addFileTo && schema && <AddFileDialog schema={schema} group={addFileTo} onSave={saveSchema} onClose={() => setAddFileTo(null)} />}
      {addingField && schema && <AddFieldDialog schema={schema} onSave={saveSchema} onClose={() => setAddingField(false)} />}
    </div>
  );
}

/** 点字段：看提取值和来源，人工填写 / 清除；管理员可改「必填」（改的是任务类型，所有任务生效） */
function FieldDialog({ taskId, chat: initialChat, onTaught, field, extracted, canEdit, canSetRequired, cats, onClose, onSave, onConfig }: {
  taskId: string; chat: FieldChatMessage[]; onTaught: () => void;
  field: ManifestField; extracted: ExtractedField | undefined; canEdit: boolean; canSetRequired: boolean; cats: ManifestFileReq[];
  onClose: () => void; onSave: (v: string | null) => Promise<void>; onConfig: (c: { required: boolean; sourceFiles: string[] }) => Promise<void>;
}) {
  const [links, setLinks] = useState<string[]>(field.sourceFiles ?? []);
  // 字段助理：告诉 AI 怎么找 → 它重找一次 → 不对继续说 → 对了「记住」
  const [chat, setChat] = useState<FieldChatMessage[]>(initialChat);
  const [msg, setMsg] = useState("");
  const [asking, setAsking] = useState(false);
  const lastAi = [...chat].reverse().find((m) => m.role === "ai");
  const ask = async () => {
    if (!msg.trim()) return;
    setAsking(true); setError(null);
    const text = msg.trim();
    setChat((c) => [...c, { role: "user", text, at: new Date().toISOString() }]);
    setMsg("");
    try { setChat((await api.teachField(taskId, field.key, text)).chat); }
    catch (e) { setError(errorText(e)); setChat((c) => c.slice(0, -1)); setMsg(text); }
    finally { setAsking(false); }
  };
  const remember = async () => {
    setAsking(true); setError(null);
    try { await api.confirmTeach(taskId, field.key); onTaught(); onClose(); }
    catch (e) { setError(errorText(e)); }
    finally { setAsking(false); }
  };
  const [v, setV] = useState(extracted?.value ?? "");
  const [required, setRequired] = useState(field.required);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); onClose(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const src = extracted?.state === "manual" ? "人工填写" : extracted?.source ? `${extracted.source.fileName.split("／").pop()}（${extracted.source.method === "ai" ? "AI 识别" : "规则"}）` : "没有提取到";
  return (
    <div className="modal-mask" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={field.label}>
        <h2>{field.label}</h2>
        {field.hint && <p className="small muted">{field.hint}</p>}
        <p className="small">当前：<b translate="no">{extracted?.value || "（空）"}</b> · 来源：{src}</p>
        {extracted?.state === "conflict" && canEdit && (
          <div className="notice warn" data-testid="conflict-pick">
            <p className="small" style={{ marginTop: 0 }}>几份文件读到的值不一样，选对的那个（选了就以它为准）：</p>
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              {[...new Map((extracted.candidates ?? []).filter((c) => c.value != null).map((c) => [String(c.value), c])).values()].map((c) => (
                <button key={String(c.value)} type="button" className="btn" disabled={busy} onClick={() => void run(() => onSave(String(c.value)))}>
                  <b translate="no">{String(c.value)}</b><span className="small muted"> · {c.fileName.split("／").pop()}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {canEdit && (
          <label className="field">
            <span className="lbl">填写 / 修改</span>
            {field.type === "enum" && field.options?.length ? (
              <select aria-label="字段值" value={v} disabled={busy} onChange={(e) => setV(e.target.value)}>
                <option value="">（请选择）</option>
                {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : field.type === "boolean" ? (
              <select aria-label="字段值" value={v} disabled={busy} onChange={(e) => setV(e.target.value)}>
                <option value="">（请选择）</option>
                <option value="true">是</option>
                <option value="false">否</option>
              </select>
            ) : field.type === "text" ? (
              <textarea aria-label="字段值" value={v} rows={3} disabled={busy} onChange={(e) => setV(e.target.value)} />
            ) : (
              <input aria-label="字段值" type={field.type === "date" ? "date" : "text"} value={v} disabled={busy} onChange={(e) => setV(e.target.value)} />
            )}
          </label>
        )}
        {/* 直接输入的字段不识别：不给 AI 助理 */}
        {canEdit && !field.inputOnly && (
          <div className="fb-chat">
            <div className="lbl">让 AI 助理识别</div>
            {field.howTo?.length ? <p className="small muted">已记住的识别方法：{field.howTo.join("；")}</p> : null}
            {chat.length === 0 && <p className="small muted">告诉它这个字段在哪份文件、长什么样、怎么换算，它会马上按你说的再找一次；不对就继续说，对了点「对了，记住」，以后所有任务都按这个方法识别。</p>}
            <div className="fb-chat-list">
              {chat.map((m, i) => (
                <div key={i} className={`fb-msg fb-msg-${m.role}`}>
                  <span className="small muted">{m.role === "user" ? "你" : "助理"}</span>
                  <div className="pre" translate="no">{m.text}</div>
                  {m.role === "ai" && m.value && <div className="small">找到：<b translate="no">{m.value}</b>{m.file ? `（${m.file.split("／").pop()}）` : ""}</div>}
                </div>
              ))}
              {asking && <div className="fb-msg fb-msg-ai small muted">助理正在按你说的重新识别…（看图片时可能要一两分钟）</div>}
            </div>
            <div className="row" style={{ gap: 6 }}>
              <input aria-label="告诉助理怎么找" placeholder="例如：看护照资料页第二行，中国护照只写省份，按国籍填" value={msg} disabled={asking} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void ask(); }} style={{ flex: 1 }} />
              <button className="btn" disabled={asking || !msg.trim()} onClick={() => void ask()}>发送</button>
              {lastAi?.value && <button className="btn btn-primary" disabled={asking} onClick={() => void remember()}>对了，记住</button>}
            </div>
          </div>
        )}
        {canSetRequired && (
          <div className="fb-chat">
            <div className="lbl">字段设置（改的是这个操作，所有任务生效）</div>
            <label className="check" style={{ marginTop: 4 }}>
              <input type="checkbox" checked={required} disabled={busy} onChange={(e) => setRequired(e.target.checked)} />
              <span>必填（取不到时提醒、挡住开始）。关掉后以后取不到也不再提醒</span>
            </label>
            {field.inputOnly ? <p className="small muted" style={{ margin: "6px 0 2px" }}>直接输入的字段：不关联文件，不自动识别</p> : <>
            <div className="small muted" style={{ margin: "6px 0 2px" }}>由哪些文件提供（都不勾 = 每次手填或写在补充说明里）</div>
            <div className="fb-pick">
              {cats.map((c) => (
                <label key={c.key} className="check">
                  <input type="checkbox" checked={links.includes(c.key)} disabled={busy} onChange={(e) => setLinks((p) => (e.target.checked ? [...p, c.key] : p.filter((k) => k !== c.key)))} />
                  <span>{c.group ? `${c.group} / ` : ""}{c.label}</span>
                </label>
              ))}
            </div>
            </>}
          </div>
        )}
        {error && <p className="notice err">{error}</p>}
        <div className="actions">
          {canEdit && extracted?.state === "manual" && <button className="btn" disabled={busy} onClick={() => void run(() => onSave(null))}>清除人工值</button>}
          <button className="btn" disabled={busy} onClick={onClose}>取消</button>
          {canSetRequired && (required !== field.required || [...links].sort().join() !== [...(field.sourceFiles ?? [])].sort().join()) && <button className="btn" disabled={busy} onClick={() => void run(() => onConfig({ required, sourceFiles: links }))}>保存字段设置</button>}
          {canEdit && <button className="btn btn-primary" disabled={busy || !v.trim() || v === (extracted?.value ?? "")} onClick={() => void run(() => onSave(v.trim()))}>保存值</button>}
        </div>
      </div>
    </div>
  );
}

/** 字节数 → 「3.7MB」/「781KB」 */
function humanSize(n: number): string {
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`;
}
